import React, { useCallback, useEffect, useState, useRef } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, UploadIcon, FileTextIcon, XIcon, CheckCircleIcon, LoaderIcon } from 'lucide-react';
import { SearchableSelect } from '../../../shared/ui/SearchableSelect';
import { mockSuppliers } from '../../suppliers/mocks/mockSuppliers';
import { mockBills } from '../mocks/mockBills';
const CURRENCIES = [getTenant().currency.code, 'EUR', 'GBP', 'SGD', 'MYR'];
interface UploadedFile {
  name: string;
  size: number;
  type: string;
}
interface ExtractedData {
  invoiceNumber: string;
  localId: string;
  dueDate: string;
  amountDue: string;
  supplierName: string;
}
export function BillForm() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const billId = searchParams.get('id');
  const prefillSupplierId = searchParams.get('supplierId');
  const isEditMode = !!billId;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editingBill = isEditMode ? mockBills.find(b => b.id === billId) || null : null;
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionDone, setExtractionDone] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [formData, setFormData] = useState({
    invoiceNumber: editingBill?.billNumber || '',
    supplierName: editingBill?.supplierName || '',
    localId: '',
    dueDate: editingBill?.dueDate || '',
    amountDue: editingBill?.amount?.toString() || '',
    currency: editingBill?.currency || getTenant().currency.code
  });
  // Pre-fill supplier from query param
  useEffect(() => {
    if (prefillSupplierId && !isEditMode) {
      const supplier = mockSuppliers.find(s => s.id === prefillSupplierId);
      if (supplier) {
        setFormData(prev => ({
          ...prev,
          supplierName: supplier.name
        }));
      }
    }
  }, [prefillSupplierId, isEditMode]);
  const supplierOptions = mockSuppliers.map(s => s.name);
  // Simulate OCR extraction from uploaded file
  const simulateExtraction = useCallback((fileName: string) => {
    setIsExtracting(true);
    setExtractionDone(false);
    // Simulate a delay for "processing"
    setTimeout(() => {
      // Mock extracted data based on file name patterns
      const mockExtracted: ExtractedData = {
        invoiceNumber: `INV-${Date.now().toString().slice(-6)}`,
        localId: `LOC-${Math.floor(Math.random() * 90000 + 10000)}`,
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        amountDue: '',
        supplierName: ''
      };
      setFormData(prev => ({
        ...prev,
        invoiceNumber: prev.invoiceNumber || mockExtracted.invoiceNumber,
        localId: prev.localId || mockExtracted.localId,
        dueDate: prev.dueDate || mockExtracted.dueDate
      }));
      setIsExtracting(false);
      setExtractionDone(true);
    }, 1800);
  }, []);
  const handleFileSelect = (file: File) => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) return;
    setUploadedFile({
      name: file.name,
      size: file.size,
      type: file.type
    });
    simulateExtraction(file.name);
  };
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [simulateExtraction]);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);
  const removeFile = () => {
    setUploadedFile(null);
    setExtractionDone(false);
    setIsExtracting(false);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/bills');
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  return <div className="max-w-xl mx-auto pb-12" data-id="element-3865">
      {/* Header */}
      <div className="mb-6" data-id="element-3866">
        <button onClick={() => navigate('/bills')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-3867">
          <ArrowLeftIcon size={16} className="mr-2" data-id="element-3868" />
          {t('pages.BillApprovalWizard.back_to_bills')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3869">
        <div className="px-8 py-6 border-b border-gray-100" data-id="element-3870">
          <h1 className="text-xl font-semibold text-gray-900" data-id="element-3871">
            {isEditMode ? 'Edit bill' : 'Add bill'}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-3872">
            {t('pages.BillForm.upload_your_invoice_and_well_extract')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6" data-id="element-3873">
          {/* File Upload Area */}
          {!uploadedFile ? <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onClick={() => fileInputRef.current?.click()} className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${isDragOver ? 'border-[#FFC70A] bg-[#FFFBE6]/50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`} data-id="element-3874">
              <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }} data-id="element-3875" />

              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 transition-colors ${isDragOver ? 'bg-[#FFC70A]/10' : 'bg-gray-100'}`} data-id="element-3876">
                <UploadIcon size={22} className={isDragOver ? 'text-[#B38A00]' : 'text-gray-500'} data-id="element-3877" />
              </div>
              <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-3878">
                {t('pages.BillForm.drag_drop_your_files_here')}
              </p>
              <p className="text-xs text-gray-500" data-id="element-3879">
                {t('pages.BillForm.supported_file_types_pdf_jpg_png')}
              </p>
            </div> /* Uploaded File Card */ : <div className="border border-gray-200 rounded-xl p-4" data-id="element-3880">
              <div className="flex items-center gap-3" data-id="element-3881">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-3882">
                  <FileTextIcon size={20} className="text-red-500" data-id="element-3883" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-3884">
                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-3885">
                    {uploadedFile.name}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-3886">
                    {formatFileSize(uploadedFile.size)}
                  </p>
                </div>
                {isExtracting ? <div className="flex items-center gap-2 text-[#B38A00]" data-id="element-3887">
                    <LoaderIcon size={16} className="animate-spin" data-id="element-3888" />
                    <span className="text-xs font-medium" data-id="element-3889">
                      {t('pages.BillForm.extracting')}
                    </span>
                  </div> : extractionDone ? <div className="flex items-center gap-1.5 text-[#B38A00]" data-id="element-3890">
                    <CheckCircleIcon size={16} data-id="element-3891" />
                    <span className="text-xs font-medium" data-id="element-3892">
                      {t('pages.BillForm.extracted')}
                    </span>
                  </div> : null}
                <button type="button" onClick={removeFile} aria-label="Remove file" className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors" data-id="element-3893">
                  <XIcon size={16} aria-hidden="true" data-id="element-3894" />
                </button>
              </div>

              {/* Extraction progress bar */}
              {isExtracting && <div className="mt-3 h-1 bg-gray-100 rounded-full overflow-hidden" data-id="element-3895">
                  <div className="h-full bg-gradient-to-r from-[#FFC70A] to-[#FFD42E] rounded-full animate-pulse w-2/3" data-id="element-3896" />
                </div>}
            </div>}

          {/* Extracted fields hint */}
          {extractionDone && <div className="bg-[#FFFBE6] border border-[#FFF4B8] rounded-lg px-4 py-3 flex items-start gap-2.5" data-id="element-3897">
              <CheckCircleIcon size={16} className="text-[#B38A00] mt-0.5 flex-shrink-0" data-id="element-3898" />

              <p className="text-xs text-gray-700 leading-relaxed" data-id="element-3899">
                {t('pages.BillForm.invoice_number_local_id_and_due')}
              </p>
            </div>}

          {/* Core Fields — always visible */}
          <div className="space-y-5" data-id="element-3900">
            {/* Supplier — always required */}
            <div data-id="element-3901">
              <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-3902">
                {t('pages.BillFinancingWizard.supplier')}{' '}
                <span className="text-red-400" data-id="element-3903">*</span>
              </label>
              <SearchableSelect name="supplierName" options={supplierOptions} value={formData.supplierName} onChange={val => setFormData(prev => ({
              ...prev,
              supplierName: val
            }))} placeholder={t('pages.BillForm.attrs.select_supplier')} data-id="element-3904" />
            </div>

            {/* Amount + Currency side by side */}
            <div className="grid grid-cols-3 gap-3" data-id="element-3905">
              <div className="col-span-2" data-id="element-3906">
                <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-3907">
                  {t('pages.BillForm.amount_due')}{' '}
                  <span className="text-red-400" data-id="element-3908">*</span>
                </label>
                <input type="number" name="amountDue" step="0.01" min="0" value={formData.amountDue} onChange={handleChange} placeholder="0.00" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" required data-id="element-3909" />
              </div>
              <div data-id="element-3910">
                <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-3911">
                  {t('pages.BillForm.currency')}
                </label>
                <SearchableSelect name="currency" options={CURRENCIES} value={formData.currency} onChange={val => setFormData(prev => ({
                ...prev,
                currency: val
              }))} placeholder={t('pages.BillApprovalWizard.usd')} data-id="element-3912" />
              </div>
            </div>

            {/* Due date */}
            <div data-id="element-3913">
              <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-3914">
                {t('pages.BillForm.due_date')}{' '}
                <span className="text-red-400" data-id="element-3915">*</span>
              </label>
              <input type="date" name="dueDate" value={formData.dueDate} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" required data-id="element-3916" />
            </div>
          </div>

          {/* Auto-extracted fields — shown after extraction or always editable */}
          {(extractionDone || isEditMode) && <div className="border-t border-gray-100 pt-5 space-y-5" data-id="element-3917">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-3918">
                {t('pages.BillForm.extracted_details')}
              </p>
              <div className="grid grid-cols-2 gap-3" data-id="element-3919">
                <div data-id="element-3920">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-3921">
                    {t('pages.BillForm.invoice_number')}
                  </label>
                  <input type="text" name="invoiceNumber" value={formData.invoiceNumber} onChange={handleChange} placeholder={t('pages.BillForm.attrs.autodetected')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" data-id="element-3922" />
                </div>
                <div data-id="element-3923">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-3924">
                    {t('pages.BillForm.local_id')}
                  </label>
                  <input type="text" name="localId" value={formData.localId} onChange={handleChange} placeholder={t('pages.BillForm.attrs.autodetected')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" data-id="element-3925" />
                </div>
              </div>
            </div>}

          {/* Actions */}
          <div className="pt-4 flex justify-end gap-3" data-id="element-3926">
            <button type="button" onClick={() => navigate('/bills')} className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-3927">
              {t('pages.BillApprovalWizard.cancel')}
            </button>
            <button type="submit" disabled={!formData.supplierName || !formData.amountDue} className="px-6 py-2.5 text-sm font-medium text-gray-900 bg-[#FFC70A] rounded-lg hover:bg-[#D9A800] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3928">
              {t('pages.BillForm.save')}
            </button>
          </div>
        </form>
      </div>
    </div>;
}